import React, { FormEvent, useEffect, useRef, useState } from "react";
import { Box, Button, Card, CardContent, CircularProgress, Link, Stack, TextField, Typography } from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import TopBar from "../../components/TopBar";
import BottomBar from "../../components/BottomBar";
import AffiliateNotice from "../../components/AffiliateNotice";
import Dashboard from "./Dashboard";
import { AffiliateAccount, affiliateApi, affiliateError, affiliateSessionExpired } from "../../util/api/affiliateApi";

export type AffiliatePageName = "register" | "register-success" | "confirm-email" | "login" | "forgot-password" | "reset-password" | "account";
const titles: Record<AffiliatePageName, string> = {
  register: "Apply as an affiliate", "register-success": "Application received", "confirm-email": "Confirm affiliate email",
  login: "Affiliate login", "forgot-password": "Recover affiliate password", "reset-password": "Reset affiliate password", account: "Affiliate dashboard",
};
const helperTextStyle = { fontSize: "0.625rem", lineHeight: 1.3, overflowWrap: "anywhere" as const };

export function passwordError(password: string): string {
  if (password.length < 14) return "Use at least 14 characters.";
  // bcrypt accepts at most 72 bytes, including multi-byte characters.
  if (new Blob([password]).size > 72) return "Use at most 72 UTF-8 bytes.";
  return "";
}

export default function AffiliatePage({ page }: { page: AffiliatePageName }) {
  const navigate = useNavigate();
  const registrationState = useLocation().state as { registrationEmail?: unknown } | null;
  const registrationEmail = typeof registrationState?.registrationEmail === "string" ? registrationState.registrationEmail : "";
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [completed, setCompleted] = useState(false);
  const [account, setAccount] = useState<AffiliateAccount | null>(null);
  const [checking, setChecking] = useState(page === "account");
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") || "");

  useEffect(() => {
    if (page === "confirm-email" || page === "reset-password") {
      window.history.replaceState(window.history.state, "", window.location.pathname);
    }
  }, [page]);

  useEffect(() => {
    if (page !== "account") return;
    let alive = true;
    affiliateApi.me().then(value => { if (alive) setAccount(value); }).catch(reason => {
      if (!alive) return;
      if (affiliateSessionExpired(reason)) navigate("/affiliate/login", { replace: true });
      else setError(affiliateError(reason));
    }).finally(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
  }, [page, navigate]);

  const run = async (action: () => Promise<void>) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true); setError(""); setMessage("");
    try { await action(); }
    catch (reason) {
      if (page === "account" && affiliateSessionExpired(reason)) navigate("/affiliate/login", { replace: true });
      else setError(affiliateError(reason));
    } finally { submitting.current = false; setBusy(false); }
  };

  const newPassword = page === "register" || page === "reset-password";
  const passwordValidation = newPassword ? passwordError(password) : "";
  const mismatch = newPassword && confirmation !== password;
  const submit = (event: FormEvent) => {
    event.preventDefault(); setAttempted(true);
    if (newPassword && (passwordValidation || mismatch)) return;
    if (page === "register" && !displayName.trim()) return;
    run(async () => {
      if (page === "register") {
        await affiliateApi.register({ display_name: displayName.trim(), email, password, password_confirm: confirmation });
        navigate("/affiliate/register-success", { state: { registrationEmail: email.trim().toLowerCase() } });
      } else if (page === "login") {
        await affiliateApi.login(email, password); navigate("/affiliate/account");
      } else if (page === "forgot-password") {
        setMessage((await affiliateApi.requestReset(email)).message);
      } else if (page === "reset-password") {
        setMessage((await affiliateApi.reset(token, password, confirmation)).message); setCompleted(true);
        setPassword(""); setConfirmation("");
      }
    });
  };

  const emailField = <TextField label="Email" type="email" autoComplete="email" required fullWidth value={email}
    onChange={event => setEmail(event.target.value)} inputProps={{ maxLength: 254 }} />;
  const formPage = ["register", "login", "forgot-password", "reset-password"].includes(page);

  return <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
    <Helmet><title>WaveMind Audio | {titles[page]}</title><meta name="robots" content="noindex, nofollow" /><meta name="referrer" content="no-referrer" /></Helmet>
    <TopBar />
    <Box component="main" sx={{ flex: 1, bgcolor: page === "account" ? "#f7f8fa" : "#fff", px: { xs: 2, sm: 4 }, py: { xs: 4, md: 8 } }}>
      <Card sx={{ maxWidth: page === "account" ? 1120 : 500, mx: "auto", bgcolor: page === "account" ? "transparent" : "#1a1a1a", borderRadius: 3,
        ...(page === "account" ? { boxShadow: "none", overflow: "visible" } : {}),
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.6)" },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
      }}>
        <CardContent sx={{ p: page === "account" ? 0 : { xs: 3, sm: 4 }, ...(page === "account" ? { "&:last-child": { pb: 0 } } : {}) }}><Stack spacing={3}>
          {(page !== "account" || !account) && <Typography component="h1" variant="h4" sx={page === "account" ? { color: "#242522" } : {}}>{titles[page]}</Typography>}
          {error && <AffiliateNotice severity="error">{error}</AffiliateNotice>}
          {message && <AffiliateNotice severity="success" role="status">{message}</AffiliateNotice>}
          {page === "register-success" && <>
            <Typography sx={{ overflowWrap: "anywhere" }}>Confirm your email using the link we sent{registrationEmail ? ` to ${registrationEmail}` : " to your signup email address"}, then wait for WaveMind support to approve your application. If the email does not arrive, check spam{registrationEmail ? " or resend it below" : " or contact WaveMind support"}.</Typography>
            {registrationEmail && <Button disabled={busy} onClick={() => run(async () => {
              setMessage((await affiliateApi.resend(registrationEmail)).message);
            })}>Resend confirmation</Button>}
          </>}
          {page === "register" && <Typography>Create an account. Email confirmation and support approval are required before you can log in.</Typography>}
          {page === "login" && <Typography>Use your email and password.</Typography>}
          {formPage && !completed && <Box component="form" onSubmit={submit}><Stack spacing={2.5}>
            {page === "register" && <TextField label="Display name" required fullWidth value={displayName} onChange={event => setDisplayName(event.target.value)}
              FormHelperTextProps={{ style: helperTextStyle }}
              error={attempted && !displayName.trim()} helperText={attempted && !displayName.trim() ? "Enter your display name." : ""} inputProps={{ maxLength: 100 }} autoComplete="name" />}
            {page !== "reset-password" && emailField}
            {page !== "forgot-password" && <TextField label="Password" type="password" required fullWidth value={password} onChange={event => setPassword(event.target.value)}
              FormHelperTextProps={{ style: helperTextStyle }}
              autoComplete={newPassword ? "new-password" : "current-password"} error={attempted && !!passwordValidation}
              helperText={newPassword ? (attempted && passwordValidation ? passwordValidation : "At least 14 characters; at most 72 UTF-8 bytes.") : ""} />}
            {newPassword && <TextField label="Confirm password" type="password" autoComplete="new-password" required fullWidth value={confirmation}
              FormHelperTextProps={{ style: helperTextStyle }}
              onChange={event => setConfirmation(event.target.value)} error={attempted && mismatch} helperText={attempted && mismatch ? "Passwords do not match." : ""} />}
            {page === "reset-password" && !token && <AffiliateNotice severity="warning">This reset link is incomplete. Open the full link in your email or request another password reset.</AffiliateNotice>}
            <Button type="submit" variant="contained" disabled={busy || (page === "reset-password" && !token)}>
              {busy ? "Please wait…" : page === "register" ? "Submit application" : page === "login" ? "Log in" : page === "forgot-password" ? "Send password reset email" : "Save new password"}
            </Button>
          </Stack></Box>}
          {page === "confirm-email" && <>
            {!token && !completed && <AffiliateNotice severity="warning">This confirmation link is incomplete. Open the full link in your confirmation email.</AffiliateNotice>}
            {!completed && <Button variant="contained" disabled={busy || !token} onClick={() => run(async () => {
              setMessage((await affiliateApi.confirm(token)).message); setCompleted(true);
            })}>{busy ? "Confirming…" : "Confirm email ownership"}</Button>}
          </>}
          {page === "account" && <>
            {checking && <CircularProgress aria-label="Checking account access" sx={{ color: "#566147" }} />}
            {!checking && !account && <Button disabled={busy} sx={{ color: "#4f5947" }} onClick={() => run(async () => setAccount(await affiliateApi.me()))}>Retry account loading</Button>}
            {account && <Dashboard account={account} busy={busy}
              onGenerate={() => run(async () => setAccount(await affiliateApi.link()))}
              onCopy={(value, kind = "link") => run(async () => {
                  try { await navigator.clipboard.writeText(value); setMessage(kind === "code" ? "Discount code copied." : "Link copied."); }
                  catch { setError(`Copy was unavailable. Select and copy the ${kind === "code" ? "discount code" : "link"} above.`); }
              })}
              onLogout={() => run(async () => { await affiliateApi.logout(); setAccount(null); navigate("/affiliate/login", { replace: true }); })} />}
          </>}
          {page !== "account" && <Stack spacing={1}>
            {page !== "login" && <Link component={RouterLink} to="/affiliate/login">Login</Link>}
            {page !== "register" && <Link component={RouterLink} to="/affiliate/register">Create an account</Link>}
            <Link component={RouterLink} to="/affiliate/forgot-password">Forgot password?</Link>
          </Stack>}
        </Stack></CardContent>
      </Card>
    </Box>
    <BottomBar />
  </Box>;
}
